import React, { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ContentBox from '../../components/ContentBox';
import { FrankoPopLayout, FpButton, EmptyState, Pagination, Select } from '../../components/frankopop/frankopopShared';
import {
    ListingCard, CreateListingModal, AcceptListingModal, OfferModal, LoginPrompt,
} from '../../components/frankopop/tradeShared';
import {
    useFrankopopData, fetchWallet, fetchSets, fetchMarketplace, withdrawOffer, SCRAP_UNIT,
} from '../../data/frankopop';

const PAGE_SIZE = 20;
const DEFAULT_COSTS = { listing: 25, trade: 10 };

export default function FrankoPopMarketplace({ userData }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const page = Math.max(1, parseInt(searchParams.get('page'), 10) || 1);
    const setId = searchParams.get('set') || '';

    const walletFetch = useCallback(() => (userData ? fetchWallet() : Promise.resolve(null)), [userData]);
    const { data: walletData, refetch: refetchWallet } = useFrankopopData(walletFetch, null);
    const { data: sets } = useFrankopopData(fetchSets, []);

    // userData is a dependency so canAccept / isMine refresh after logging in.
    const marketFetch = useCallback(
        () => fetchMarketplace({ page, pageSize: PAGE_SIZE, setId: setId || null }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [page, setId, userData]
    );
    const { data, loading, error, refetch } = useFrankopopData(marketFetch, null);

    const [posting, setPosting] = useState(false);
    const [trading, setTrading] = useState(null);
    const [offering, setOffering] = useState(null);

    const costs = data?.costs || DEFAULT_COSTS;
    const scraps = walletData ? walletData.scraps : null;

    const updateParams = (next) => {
        const params = {};
        if (next.set) params.set = next.set;
        if (next.page && next.page > 1) params.page = String(next.page);
        setSearchParams(params);
    };
    const goToPage = (next) => {
        if (!data || next < 1 || next > data.totalPages) return;
        updateParams({ set: setId, page: next });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const afterChange = () => { refetch(); refetchWallet(); };

    const handleWithdraw = async (offerId) => {
        const res = await withdrawOffer(offerId);
        if (!res.success) { toast.error(res.message); return; }
        toast.success('Offer withdrawn.');
        refetch();
    };

    return (
        <FrankoPopLayout active="marketplace" wallet={walletData ? walletData.cards : null} scraps={userData ? scraps : undefined}>
            <ContentBox theme="default" tint="darkcyan" header="Trading board">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
                    <div className="text-sm text-gray-600 max-w-xl">
                        <p>
                            Swap FrankoPops with other collectors. If you have everything a listing asks for, trade straight away -
                            otherwise make an offer from your own collection.
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                            Posting a trade costs {costs.listing} {SCRAP_UNIT}. Completing a trade costs the receiver {costs.trade} {SCRAP_UNIT}.
                        </p>
                    </div>
                    {userData ? (
                        <FpButton variant="primary" onClick={() => setPosting(true)}>Post a trade</FpButton>
                    ) : (
                        <LoginPrompt />
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-2 mb-4">
                    <span className="font-['Jersey_10'] uppercase text-[13px] tracking-[1px] text-gray-600">Set</span>
                    <div className="w-full sm:w-64">
                        <Select value={setId} onChange={(e) => updateParams({ set: e.target.value, page: 1 })}>
                            <option value="">All sets</option>
                            {(sets || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </Select>
                    </div>
                </div>

                {loading && !data ? (
                    <p className="text-sm text-gray-500">Loading listings...</p>
                ) : error ? (
                    <p className="text-sm text-red-600">Couldn&apos;t load the trading board - {error}</p>
                ) : !data || data.listings.length === 0 ? (
                    <EmptyState
                        title={setId ? 'No trades for this set' : 'No trades yet'}
                        sub={userData ? 'Be the first to put something up for trade.' : 'Check back soon.'}
                    />
                ) : (
                    <>
                        <div className={`space-y-3 ${loading ? 'opacity-60' : ''}`}>
                            {data.listings.map((listing) => (
                                <ListingCard
                                    key={listing.id}
                                    listing={listing}
                                    loggedIn={!!userData}
                                    onTrade={setTrading}
                                    onOffer={setOffering}
                                    onWithdrawOffer={handleWithdraw}
                                />
                            ))}
                        </div>
                        <Pagination page={data.page} totalPages={data.totalPages} total={data.total} onChange={goToPage} noun="listing" />
                    </>
                )}
            </ContentBox>

            {posting && (
                <CreateListingModal
                    scraps={scraps}
                    cost={costs.listing}
                    onClose={() => setPosting(false)}
                    onCreated={() => { setPosting(false); afterChange(); }}
                />
            )}
            {trading && (
                <AcceptListingModal
                    listingId={trading.id}
                    scraps={scraps}
                    cost={costs.trade}
                    onClose={() => setTrading(null)}
                    onDone={() => { setTrading(null); afterChange(); }}
                />
            )}
            {offering && (
                <OfferModal
                    listing={offering}
                    scraps={scraps}
                    cost={costs.trade}
                    onClose={() => setOffering(null)}
                    onDone={() => { setOffering(null); afterChange(); }}
                />
            )}
        </FrankoPopLayout>
    );
}
